import React, {useCallback, useMemo, useState} from "react";
import "./index.scss";

import {connect} from "react-redux";
import {LoggedInLayout} from "features/layouts/pages/logged-in";
import {startSingleThread} from "store/reducer/threads/single/reducer";
import {singleThreadSelector} from "store/reducer/threads/single/selectors";
import Title from "features/components/title";
import Text from "features/components/text";
import {ISO_8601} from "moment";
import moment from "moment";
import {__} from "services/i18n";
import {LoadingLogoPortal} from "features/components/loading-logo";
import {Icon} from "features/components/icon";
import {LikeBox} from "features/components/like-box";
import CommentField from "features/components/comment-field";
import {commentEditShowModal} from "features/modals/comment-edit/selectors";
import {likeThreadStart} from "store/reducer/threads/like/reducer";
import {likeThreadErrorSelector} from "store/reducer/threads/like/selectors";
import {withToastManager} from "react-toast-notifications";
import CommentBox from "features/components/comment-box";
import {ReportAProblemButton} from "features/components/problem-button";
import {CategoryTagBox} from "features/components/category-tag-box";
import {historyObject} from "historyObject";
import {favoriteThreadStart} from "store/reducer/threads/favorite/reducer";
import {SpeakerBox} from "features/components/speakers";
import Button from "features/components/button";
import ThreadArchiveModal from "features/modals/thread-archive";
import {threadArchiveSetShow} from "features/modals/thread-archive/reducer";
import SuggestedResource from "features/components/suggested";
import {COMMENTS_ORDER_BY, MOBILE_MEDIA_QUERY_THRESHOLD} from "constants.js";
import {BestAnswer} from "features/components/best-answer";
import CommentAttachmentModal from "features/modals/comment-attachment";
import {commentShowAttachmentModalSelector} from "features/components/comment-field/selectors";
import {useMediaQuery} from "services/utils/utils";

/* istanbul ignore next */
const mapStateToProps = state => ({
    thread: singleThreadSelector(state),
    should_show_edit_modal: commentEditShowModal(state),
    like_error: likeThreadErrorSelector(state),
    show_attachment_modal: commentShowAttachmentModalSelector(state)
});
/* istanbul ignore next */
const mapDispatchToProps = {
    startSingleThread,
    likeThreadStart,
    favoriteThreadStart,
    threadArchiveSetShow
};


export const SingleThreadPage = (props) => {
    const {thread, should_show_edit_modal, like_error, show_attachment_modal} = props;
    const {startSingleThread, likeThreadStart, favoriteThreadStart, threadArchiveSetShow} = props;
    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY_THRESHOLD);

    React.useEffect(() => {
        startSingleThread({id: props.match.params.id})
    }, [props.match.params.id, startSingleThread]);

    React.useEffect(() => {
        if(!like_error.inError) return;
        props.toastManager.add(__(like_error.error), { appearance: 'error', id: 'error-like-video-toast'});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [like_error]);

    const breadcrumbTitle = useMemo(() => {
        if(isMobile) return "single-thread.breadcrumb";
        return thread ? thread.title : "single-thread.breadcrumb"
    }, [thread, isMobile]);


    const renderAttachment = useMemo(() => {
        if(!thread || !thread.attachment) return null;
        return <div className={"attachment-item"} onClick={() => window.open(thread.attachment)}>
            <Title size={16}>{__("single-thread.attachment")}</Title>
            <Icon>eye</Icon>
        </div>
    }, [thread]);

    const toggleThreadLike = useCallback(() => {
        likeThreadStart({id: thread.id, like: !thread.like});
    }, [likeThreadStart, thread]);

    const [favorite, setFavorite] = useState((thread || {}).favorite);
    React.useEffect(() => {
        if(!thread) return;
        setFavorite(thread.favorite);
    }, [thread]);
    const toggleFavorite = useCallback((event) => {
        setFavorite(!favorite);
        favoriteThreadStart({id: thread.id, favorite: !thread.favorite});
        event.preventDefault();
        event.stopPropagation();
    }, [favorite, thread, favoriteThreadStart]);


    const renderUserButtons = useMemo(() => {
        if(!thread || thread.is_archived || !thread.is_mine ) return null;
        return <div className={"buttons"}>
            <Button title={__("buttons.edit")} onClick={() => historyObject.push(`/threads/edit/${thread.id}`)} type={"outlined"} icon={"edit"}/>
            <Button title={__("buttons.archive")} onClick={() => threadArchiveSetShow(true)} type={"accent"} icon={"archive"}/>
        </div>
    }, [thread, threadArchiveSetShow]);


    const renderCommentField = useMemo(() => {
        if (!thread || should_show_edit_modal || show_attachment_modal || thread.is_archived) return null;
        if(!thread.can_comment) {
            return <Text size={16}>{__("single-thread.cant-comment")}</Text>
        }
        return <CommentField type={"thread"} resource_id={thread.id} enable_attachment={true} />
    }, [should_show_edit_modal, thread, show_attachment_modal]);


    const renderThreadData = useMemo(() => {
        if (!thread) return null;
        return <>
            <Title size={34}>{thread.title}</Title>
            <Text color={"light"} size={14}>{__(thread.is_archived ? "single-thread.archived-on" : "single-thread.last-updated", [{key: "[date]", value: moment(thread.update_date, ISO_8601).format("DD MMMM YYYY")}])}</Text>
            <Text size={16}>{thread.message}</Text>
            {renderAttachment}
            </>
    }, [renderAttachment, thread]);

    const renderContent = useMemo(() => {
        if(!thread) {
            return <LoadingLogoPortal />
        }
        return <div className={"sidebar-wrapper"}>
            <div className="content">
                {!isMobile && renderThreadData}
                <div className={"like-comment"}>
                    <LikeBox onLike={toggleThreadLike} total={thread.likes.total} like={thread.like} avatars={thread.likes.avatars} />
                    {renderCommentField}
                </div>
                <CommentBox type={"thread"} resource_id={thread.id} enable_voting={true} enable_sorting={true} sorting_options={COMMENTS_ORDER_BY} />
                {isMobile && <ReportAProblemButton />}
            </div>
            <div className="sidebar">
                {isMobile && renderThreadData}
                <div className="row favorite-name">
                    <div className="name">
                        <Text size={16}>{__(`single-thread.type.${thread.type}`)}</Text>
                    </div>
                    <div className="favorite-btn" onClick={toggleFavorite}>
                        <Icon>{favorite ? "heart-fill": "heart"}</Icon>
                    </div>
                </div>
                <CategoryTagBox categories={thread.categories} tags={[]} />
                <SpeakerBox speakers={[{avatar: thread.avatar, name: thread.user_name, title: __("single-thread.published-on", [{key: "[date]", value: moment(thread.creation_date, ISO_8601).format("DD MMMM YYYY")}])}]} />
                {renderUserButtons}
                <BestAnswer comment={thread.best_answer} archived={thread.is_archived} />
                {!isMobile && <ReportAProblemButton />}
            </div>
        </div>
    }, [thread, isMobile, renderThreadData, toggleThreadLike, renderCommentField, toggleFavorite, favorite, renderUserButtons])
    
    
    
    return <LoggedInLayout
        type={"light"}
        className={"single-thread-page"}
        breadcrumbs={[{title: "common.home-breadcrumb", link:"/"}, {title: "threads.breadcrumb", link: "/threads"}, {title: breadcrumbTitle}]}>
            {renderContent}
            <ThreadArchiveModal id={(thread || {}).id} />
            {!!thread && <SuggestedResource resource_id={thread.id} type={"thread"}/>}
            {show_attachment_modal && <CommentAttachmentModal type={"thread"} resource_id={(thread || {}).id} />}
    </LoggedInLayout>
}
export default connect(mapStateToProps, mapDispatchToProps)(withToastManager(SingleThreadPage));